/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §4.3
 * @standard docs/standards/staking-ui-wallet-actions.md#ui-gate
 *
 * The UI side of decide(): one form's (or the header pill's) decision plus its single
 * useNetworkSwitch instance, the engine behind "ask" (user-approved switch, add-chain fallback).
 * `none` and `switch-silently` render nothing; the embedded wallet never sees network UI.
 */
import { useCallback } from 'react';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { useNetworkSwitch } from '@/hooks/useNetworkSwitch';
import { decide } from '@/lib/wallet-actions/decide';
import type { Decision, WalletAction } from '@/lib/wallet-actions/types';
import { getChainDisplayName } from '@/lib/chain-registry';
import type { WalletKind } from '@/lib/wallet-kind';

export interface UseActionGateReturn {
  decision: Decision;
  currentChainId: number | null;
  currentChainName: string | null;
  requiredChainName: string;
  switchToRequired: () => Promise<boolean>;
  isSwitching: boolean;
  error: string | null;
  switchHint: string | null;
  wallet: ConnectedWallet | null;
  walletKind: WalletKind;
}

export function useActionGate(
  action: WalletAction,
  wallet?: ConnectedWallet | null
): UseActionGateReturn {
  // undefined (argument absent) = selected wallet, null = no wallet: forwarded as-is.
  const ns = useNetworkSwitch(wallet);

  // Several component tests replace useNetworkSwitch with partial stubs; a missing field reads as
  // "unknown", which never gates.
  const resolvedWallet = ns.wallet ?? null;
  const walletKind = ns.walletKind ?? 'unknown';
  const currentChainId = ns.currentChainId ?? null;
  const decision: Decision = resolvedWallet ? decide(walletKind, currentChainId, action) : 'none';

  const requiredChainId = action.chainId;
  const { switchNetwork } = ns;
  const switchToRequired = useCallback(
    async () => (requiredChainId === null ? true : switchNetwork(requiredChainId)),
    [switchNetwork, requiredChainId]
  );

  return {
    decision,
    currentChainId,
    currentChainName: currentChainId === null ? null : getChainDisplayName(currentChainId),
    requiredChainName: requiredChainId === null ? '' : getChainDisplayName(requiredChainId),
    switchToRequired,
    isSwitching: ns.isSwitching ?? false,
    error: ns.error ?? null,
    switchHint: ns.switchHint ?? null,
    wallet: resolvedWallet,
    walletKind,
  };
}
